/**
 * Phase 12 — quality records, corrective actions, maintenance, notifications
 * and data import (decisions D161–D178).
 *
 * Every schema is `.strict()`: an unknown field is refused, never ignored.
 * NO FIELD HERE DESCRIBES A MATERIAL (CLAUDE.md section 4).
 */
import { z } from 'zod';
import { ALL_CORRECTIVE_ACTION_STATUSES, ALL_IMPORT_KINDS, ALL_MAINTENANCE_KINDS, ALL_MAINTENANCE_STATUSES, ALL_NOTIFICATION_KINDS, ALL_QUALITY_RECORD_KINDS, ALL_QUALITY_RECORD_STATUSES, IMPORT_LIMITS, } from '@lims/shared-types';
import { boundedText, isoDateSchema, listQuerySchema, reasonSchema, uuidSchema } from './primitives.js';
/** An optional free-text field: absent, or actual content. Never blank. */
const optionalText = (max) => z
    .string()
    .trim()
    .max(max)
    .nullable()
    .optional()
    .transform((value) => (value === '' ? null : value));
const enumOf = (values) => z.enum(values);
const booleanQuery = z
    .enum(['true', 'false'])
    .default('false')
    .transform((value) => value === 'true');
export const qualityRecordKindSchema = enumOf(ALL_QUALITY_RECORD_KINDS);
export const qualityRecordStatusSchema = enumOf(ALL_QUALITY_RECORD_STATUSES);
export const correctiveActionStatusSchema = enumOf(ALL_CORRECTIVE_ACTION_STATUSES);
export const maintenanceKindSchema = enumOf(ALL_MAINTENANCE_KINDS);
export const maintenanceStatusSchema = enumOf(ALL_MAINTENANCE_STATUSES);
export const notificationKindSchema = enumOf(ALL_NOTIFICATION_KINDS);
export const importKindSchema = enumOf(ALL_IMPORT_KINDS);
// ---------------------------------------------------------------------------
// Quality records (D163)
// ---------------------------------------------------------------------------
export const createQualityRecordSchema = z
    .object({
    kind: qualityRecordKindSchema,
    title: boundedText(3, 200),
    description: boundedText(3, 4000),
    clientId: uuidSchema.nullable().optional(),
    workOrderId: uuidSchema.nullable().optional(),
    equipmentId: uuidSchema.nullable().optional(),
    /** When it was raised. Never after the laboratory's today (API check). */
    raisedOn: isoDateSchema.optional(),
})
    .strict();
export const updateQualityRecordSchema = z
    .object({
    title: boundedText(3, 200).optional(),
    description: boundedText(3, 4000).optional(),
    responsibleUserId: uuidSchema.nullable().optional(),
    dueOn: isoDateSchema.nullable().optional(),
    rootCause: optionalText(4000),
})
    .strict()
    .refine((value) => Object.keys(value).length > 0, {
    message: 'Nothing to change.',
});
export const closeQualityRecordSchema = z
    .object({
    closureSummary: boundedText(3, 4000),
    closedOn: isoDateSchema,
})
    .strict();
export const cancelRecordSchema = z
    .object({
    reason: reasonSchema,
})
    .strict();
export const qualityRecordListQuerySchema = listQuerySchema.extend({
    kind: qualityRecordKindSchema.optional(),
    status: qualityRecordStatusSchema.optional(),
    responsibleUserId: uuidSchema.optional(),
});
// ---------------------------------------------------------------------------
// Corrective actions (D165)
// ---------------------------------------------------------------------------
export const createCorrectiveActionSchema = z
    .object({
    description: boundedText(3, 4000),
    responsibleUserId: uuidSchema,
    dueOn: isoDateSchema,
})
    .strict();
export const completeCorrectiveActionSchema = z
    .object({
    completionNote: boundedText(3, 4000),
    completedOn: isoDateSchema,
})
    .strict();
export const verifyCorrectiveActionSchema = z
    .object({
    verificationNote: boundedText(3, 4000),
    verifiedOn: isoDateSchema,
})
    .strict();
export const correctiveActionListQuerySchema = listQuerySchema.extend({
    status: correctiveActionStatusSchema.optional(),
    /** Only the caller's own actions — resolved from the session, never an id. */
    mine: booleanQuery,
    overdue: booleanQuery,
});
// ---------------------------------------------------------------------------
// Maintenance and faults (D167)
// ---------------------------------------------------------------------------
export const scheduleMaintenanceSchema = z
    .object({
    equipmentId: uuidSchema,
    description: boundedText(3, 2000),
    scheduledFor: isoDateSchema,
})
    .strict();
export const reportFaultSchema = z
    .object({
    equipmentId: uuidSchema,
    description: boundedText(3, 2000),
})
    .strict();
export const completeMaintenanceSchema = z
    .object({
    performedOn: isoDateSchema,
    outcomeNote: boundedText(3, 4000),
    /** The next one, written by the person who did this one (D167). */
    nextScheduledFor: isoDateSchema.nullable().optional(),
})
    .strict()
    .refine((value) => !value.nextScheduledFor || value.nextScheduledFor > value.performedOn, {
    message: 'The next maintenance must be after this one.',
    path: ['nextScheduledFor'],
});
export const resolveFaultSchema = z
    .object({
    resolvedOn: isoDateSchema,
    resolutionNote: boundedText(3, 4000),
})
    .strict();
export const maintenanceListQuerySchema = listQuerySchema.extend({
    kind: maintenanceKindSchema.optional(),
    status: maintenanceStatusSchema.optional(),
    equipmentId: uuidSchema.optional(),
    /** Planned entries scheduled on or before this date. */
    dueBy: isoDateSchema.optional(),
});
// ---------------------------------------------------------------------------
// Notifications (D171, D172)
// ---------------------------------------------------------------------------
export const notificationListQuerySchema = listQuerySchema
    .pick({ page: true, pageSize: true })
    .extend({ unreadOnly: booleanQuery });
export const updateNotificationPreferencesSchema = z
    .object({
    preferences: z
        .array(z
        .object({
        kind: notificationKindSchema,
        inAppEnabled: z.boolean(),
        emailEnabled: z.boolean(),
    })
        .strict())
        .min(1)
        .max(ALL_NOTIFICATION_KINDS.length)
        .refine((items) => new Set(items.map((item) => item.kind)).size === items.length, {
        message: 'Each kind may appear once.',
    }),
})
    .strict();
// ---------------------------------------------------------------------------
// Data import (D175)
// ---------------------------------------------------------------------------
export const importRequestSchema = z
    .object({
    csv: z.string().min(1).max(IMPORT_LIMITS.MAX_BYTES),
})
    .strict();
//# sourceMappingURL=operations.js.map